import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { AdminHeader } from "./AdminHeader";
import "./Layout.css";
import { Sidebar } from "./Sidebar";

interface LayoutProps {
  children: React.ReactNode;
  environment?: string;
  version?: string;
}

export const Layout: React.FC<LayoutProps> = ({
  children,
  environment = "development",
  version = "1.0.0",
}) => {
  const { isAuthenticated } = useAuth();

  // サイドバー開閉状態の永続化
  const SIDEBAR_OPEN_KEY = "sidebar-open-state";

  // サイドバー表示の閾値（CSSのメディアクエリと一致させる）
  const SIDEBAR_BREAKPOINT = 769;

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== "undefined") {
      // ローカルストレージから状態を復元
      const savedState = localStorage.getItem(SIDEBAR_OPEN_KEY);
      if (savedState !== null) {
        return JSON.parse(savedState);
      }
      // 初回は画面サイズに応じて決定
      return window.innerWidth >= SIDEBAR_BREAKPOINT;
    }
    return false;
  });

  // 画面サイズ変更時の処理（モバイルでは強制的に閉じる）
  React.useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= SIDEBAR_BREAKPOINT;
      // モバイルサイズの場合のみ強制的に閉じる
      if (!isDesktop && sidebarOpen) {
        setSidebarOpen(false);
        localStorage.setItem(SIDEBAR_OPEN_KEY, JSON.stringify(false));
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [sidebarOpen]);

  const toggleSidebar = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    // ローカルストレージに状態を保存
    localStorage.setItem(SIDEBAR_OPEN_KEY, JSON.stringify(newState));
  };

  // 認証されていない場合は、ヘッダーとサイドバーを表示しない（ローカル環境でもログイン必須）
  const shouldShowLayout = isAuthenticated;
  if (!shouldShowLayout) {
    return <div className="layout">{children}</div>;
  }

  return (
    <div className="layout">
      <AdminHeader environment={environment} version={version} />
      <div className="layout-body">
        <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />
        <main className={`layout-main ${sidebarOpen ? "sidebar-open" : ""}`}>
          <div className="layout-content">{children}</div>
        </main>
      </div>
    </div>
  );
};
